
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import ProjectMixin from '@/_mixins/project'
import {
    PROJECT_QUERY,
    PROJECT_ACCESS_GROUPS_QUERY,
    PROJECT_ACCESS_GROUPS_READERS_RULES_QUERY,
} from '@/views/Project/access/gql/queries.ts'
import { DELETE_ACCESS_GROUP } from '@/views/Project/access/gql/mutations.ts'
import { accessClient } from '@/apolloClient'
import { openDialog } from '@/_components/dialog/dialog'
import GroupDialog from '@/views/Project/access/groupDialog.vue'
import NewGroupDialog from '@/views/Project/access/newGroupDialog.vue'
import { Project } from '@/views/Project/access/types'
import { cloneDeep } from 'lodash'
import ConfirmationDialog from '@/views/Project/ch/components/confirmationDialog.vue'
import AddButton from '@/_components/button/add/add.vue'

export default {
    name: 'AccessSettingsRules',
    mixins: [ProjectMixin],
    components: {
        AddButton,
        AppToolbar,
    },
    data() {
        return {
            saveStatus: null as Maybe<'saved' | 'saving'>,
            project: null as Maybe<Project>,
            isFetching: false,
            isDeletingGroup: false,
            allGroupsForProject: [] as any[],
            allRulesForProject: [] as any[],
            allReadersForProject: [] as any[],
            allAccessGroupCardAssociationsForProject: [] as any,
            allGroupReaderRuleAssociationsForProject: [] as any[],
        }
    },
    computed: {
        groupCardByGroupId() {
            return Object.groupBy(this.allAccessGroupCardAssociationsForProject, (accessGroup) => {
                return accessGroup.groupId
            })
        },
        groupReaderRuleByGroupId() {
            return Object.groupBy(this.allGroupReaderRuleAssociationsForProject, (group) => {
                return group.groupId
            })
        },
        allVisibleGroupsForProject() {
            return this.allGroupsForProject.filter((group) => group.type !== 'ADMIN')
        },
        allVisibleRulesForProject() {
            return this.allRulesForProject.filter((rule) => rule.type !== 'ADMIN')
        },
        groups() {
            return this.allGroupsForProject.map((group) => {
                const _rules = new Map()
                let _hasAnyRulesApplied = false
                this.allRulesForProject.forEach((rule) => {
                    const hasAnyRulesApplied = Boolean(
                        this.groupReaderRuleByGroupId[group.id]?.filter(
                            (groupRule) =>
                                groupRule.ruleId === rule.id &&
                                this.allReadersForProject.some(
                                    (reader) => reader.readerId === groupRule.readerId
                                )
                        ).length
                    )
                    if (hasAnyRulesApplied) {
                        _hasAnyRulesApplied = true
                    }
                    _rules.set(rule.id, hasAnyRulesApplied)
                })
                return {
                    ...group,
                    _hasAnyRulesApplied,
                    _rules,
                    _cards:
                        this.groupCardByGroupId[group.id]?.map((groupCard) => {
                            const _id = this.getCardId(groupCard.card)
                            return {
                                ...groupCard.card,
                                _id,
                                _userName:
                                    groupCard.card.bklrUser.name ||
                                    this.$t(`fe_access['text.visitor_card_number']`, {
                                        number: _id,
                                    }),
                                _companyName:
                                    groupCard.card.company?.name ||
                                    this.$t(`fe_access['text.visitor_card']`),
                                _isVisitor: groupCard.card.type === 'VISITOR',
                            }
                        }) || [],
                }
            })
        },
    },
    methods: {
        async getProjectGroups() {
            const projectResult = await accessClient.query({
                query: PROJECT_QUERY,
                variables: { projectId: this.currentProjectId },
                fetchPolicy: 'network-only',
            })
            this.project = projectResult.data.project
            const groupQueryResult = await accessClient.query({
                query: PROJECT_ACCESS_GROUPS_QUERY,
                variables: {
                    projectId: this.currentProjectId,
                    withPsi: Boolean(this.project.settings.denyWithoutPsi),
                },
                fetchPolicy: 'network-only',
            })
            this.allGroupsForProject = cloneDeep(groupQueryResult.data.allGroupsForProject)
            this.allRulesForProject = groupQueryResult.data.allRulesForProject
            this.allAccessGroupCardAssociationsForProject =
                groupQueryResult.data.allAccessGroupCardAssociationsForProject
            this.allGroupReaderRuleAssociationsForProject =
                groupQueryResult.data.allGroupReaderRuleAssociationsForProject
            this.allReadersForProject = groupQueryResult.data.allReadersForProject
        },
        getCardId(card: any) {
            return card.type === 'VIRTUAL'
                ? this.formatPhoneNumber(card.cardId)
                : card.cardId.padStart(4, '0')
        },
        async addNewGroup() {
            const dialog = await openDialog(NewGroupDialog)
            if (dialog?.addedGroup) {
                this.allGroupsForProject.push(dialog.addedGroup)
            }
        },
        async openGroup(group: any) {
            const isAdminGroup = group.type === 'ADMIN'
            const dialog = await openDialog(GroupDialog, {
                group,
                rules: isAdminGroup
                    ? this.allRulesForProject.filter((rule) => rule.type === 'ADMIN')
                    : this.allVisibleRulesForProject,
                readerRules: this.groupReaderRuleByGroupId[group.id],
                readers: this.allReadersForProject,
                disabled: isAdminGroup,
            })
            if (dialog?.updatedGroup) {
                const updatedGroup = cloneDeep(dialog.updatedGroup)
                this.allGroupsForProject = this.allGroupsForProject.map((groupItem) => {
                    if (groupItem.id === updatedGroup.id) {
                        return updatedGroup
                    }
                    return groupItem
                })
            }
            if (dialog?.ruleReaderUpdated) {
                const result = await accessClient.query({
                    query: PROJECT_ACCESS_GROUPS_READERS_RULES_QUERY,
                    variables: {
                        projectId: this.currentProjectId,
                    },
                    fetchPolicy: 'network-only',
                })
                this.allGroupReaderRuleAssociationsForProject =
                    result.data.allGroupReaderRuleAssociationsForProject
            }
        },
        async deleteAccessGroup(group) {
            if (this.isDeletingGroup) {
                return
            }

            const confirm = await openDialog(ConfirmationDialog, {
                message: `fe_access['group.message_delete_access_group']`,
                title: this.$t(`fe_access['group.text_delete_access_group']`),
                name: group.name,
            })

            if (!confirm) {
                return
            }

            try {
                this.isDeletingGroup = true
                await accessClient.mutate({
                    mutation: DELETE_ACCESS_GROUP,
                    variables: { groupId: group.id },
                })
                this.allGroupsForProject = this.allGroupsForProject.filter(
                    (groupItem) => groupItem.id !== group.id
                )
            } finally {
                this.isDeletingGroup = false
            }
        },
    },
    async mounted() {
        try {
            this.isFetching = true
            await this.getProjectGroups()
        } finally {
            this.isFetching = false
        }
    },
}
